import React, { useState, useEffect, useRef } from "react";
import { Appointment, Patient } from "../types";
import { 
  BookOpen, 
  Bell, 
  Volume2, 
  VolumeX, 
  MessageSquare, 
  Clock, 
  User, 
  Calendar, 
  Edit3, 
  Save, 
  AlertCircle,
  Sparkles,
  RefreshCw
} from "lucide-react";

interface AisNotepadProps {
  appointments: Appointment[];
  patients: Patient[];
  apiToken: string;
  setSelectedPatient: (patient: Patient | null) => void;
}

export default function AisNotepad({ appointments, patients, apiToken, setSelectedPatient }: AisNotepadProps) {
  const [muted, setMuted] = useState<boolean>(() => {
    const cached = localStorage.getItem("ais_notepad_muted");
    return cached === "true";
  });
  const [selectedApptId, setSelectedApptId] = useState<string>("");
  const [notepadContent, setNotepadContent] = useState<string>("");
  const [testSoundActive, setTestSoundActive] = useState(false);
  const [alertLogs, setAlertLogs] = useState<{ id: string; msg: string; timestamp: Date }[]>([]);
  const [showPastArchive, setShowPastArchive] = useState(false);
  
  // Track already alerted appointments to prevent double warnings
  const alertedRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    localStorage.setItem("ais_notepad_muted", String(muted));
  }, [muted]);

  // Load note whenever an appointment is selected
  useEffect(() => {
    if (selectedApptId) {
      const savedNote = localStorage.getItem(`appt_note_${selectedApptId}`) || "";
      setNotepadContent(savedNote);
    } else {
      setNotepadContent("");
    }
  }, [selectedApptId]);

  // Save changes to current note
  const handleSaveNote = () => {
    if (selectedApptId) {
      localStorage.setItem(`appt_note_${selectedApptId}`, notepadContent);
      // Log alert
      addLogEntry("system", "📝 تم حفظ وتدوين ملاحظة الموعد بنجاح.");
    }
  };

  const addLogEntry = (id: string, msg: string) => {
    setAlertLogs(prev => [
      { id: `${id}_${Date.now()}`, msg, timestamp: new Date() },
      ...prev.slice(0, 20)
    ]);
  };

  // Helper: Play high-quality synthesized sound chime
  const playChime = () => {
    if (muted) return;
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      
      const playTone = (freq: number, startTime: number, duration: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, startTime);
        
        gain.gain.setValueAtTime(0.18, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      
      const now = ctx.currentTime;
      // D5 -> F#5 -> A5 (Warm major arpeggio)
      playTone(587.33, now, 0.45);
      playTone(739.99, now + 0.15, 0.5);
      playTone(880.00, now + 0.3, 0.65);
    } catch (err) {
      console.error("Audio Context Playback error:", err);
    }
  };

  const handleTestSound = () => {
    setTestSoundActive(true);
    playChime();
    addLogEntry("test", "🔊 تم فحص واختبار التنبيه الصوتي للطبيب بنجاح.");
    setTimeout(() => setTestSoundActive(false), 800);
  };

  // Cron/Ticker to check for appointments starting within 1 minute
  useEffect(() => {
    const checkUpcomingAppointments = async () => {
      const now = Date.now();
      
      for (const appt of appointments) {
        // Only active/pending appointments
        if (appt.status !== "PENDING") continue;
        if (!appt.dateTime) continue;

        const apptTime = new Date(appt.dateTime).getTime();
        const diffMs = apptTime - now;
        
        // Define exactly "1 minute before" window as: 30 seconds to 90 seconds in the future
        const isOneMinuteBefore = diffMs >= 30000 && diffMs <= 90000;
        
        if (isOneMinuteBefore && !alertedRef.current[appt.id]) {
          // Mark immediately to ensure single alert
          alertedRef.current[appt.id] = true;
          
          const patientName = patients.find(p => p.id === appt.patientId)?.name || "مراجع غير مسجل";
          const patientPhone = patients.find(p => p.id === appt.patientId)?.phone || "";
          const pLocalTime = new Date(appt.dateTime).toLocaleTimeString("ar-IQ", { hour: "numeric", minute: "2-digit" });

          // 1. Audio alert
          playChime();
          const alertMessage = `⏰ تنبيه فوري: موعد المريض (${patientName}) سيبدأ بعد دقيقة واحدة في تمام الساعة ${pLocalTime}!`;
          addLogEntry(appt.id, alertMessage);

          // 2. WhatsApp Alert (via our newly deployed backend API)
          try {
            const response = await fetch("/api/appointments/notify-whatsapp-reception", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiToken}`
              },
              body: JSON.stringify({
                patientName,
                dateTime: appt.dateTime,
                phone: patientPhone
              })
            });
            if (response.ok) {
              const resData = await response.json();
              addLogEntry(appt.id + "_wa", `📱 تم توجيه تنبيه واتساب فوري لغرفة الاستقبال والعيادة: ${resData.status}`);
            }
          } catch (err) {
            console.error("WhatsApp notification dispatch error in notepad:", err);
          }
        }
      }
    };

    // Run interval every 8 seconds to ensure precision matching
    const timerId = setInterval(checkUpcomingAppointments, 8000);
    return () => clearInterval(timerId);
  }, [appointments, patients, apiToken, muted]);

  // Filter and sort active clinical appointments for easy visibility
  const sortedAppts = [...appointments]
    .filter(a => a.status === "PENDING" || a.status === "COMPLETED")
    .map(a => {
      const patient = patients.find(p => p.id === a.patientId);
      return {
        ...a,
        patientName: patient?.name || "مراجع افتراضي",
        phone: patient?.phone || "",
        parsedDate: new Date(a.dateTime)
      };
    })
    .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

  // ADDITIVE CLINICAL RULES: Partitioning appointments into future (upcoming) vs past or completed/done.
  const currentTimeStamp = Date.now();
  const upcomingAppts = sortedAppts.filter(a => a.status === "PENDING" && a.parsedDate.getTime() >= currentTimeStamp);

  const todayDate = new Date();
  const isSameDay = (d: Date) => 
    d.getFullYear() === todayDate.getFullYear() && 
    d.getMonth() === todayDate.getMonth() && 
    d.getDate() === todayDate.getDate();

  const pastAndCompletedAppts = sortedAppts.filter(a => 
    a.status === "PENDING" && 
    a.parsedDate.getTime() < currentTimeStamp && 
    localStorage.getItem(`appt_opened_${a.id}`) !== "true" &&
    isSameDay(a.parsedDate)
  );

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-xs overflow-hidden text-right" dir="rtl">
      {/* Header Bar */}
      <div className="bg-[#0F172A] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-indigo-600/30 p-1.5 rounded-lg text-indigo-400">
            <BookOpen className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs font-black tracking-wide uppercase">مفكرة المواعيد والخطط السريرية</h3>
            <p className="text-[9.5px] text-slate-400 font-semibold">مراقبة ذكية فورية • تنبيهات صوتية وواتساب</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5" dir="ltr">
          <button 
            type="button"
            onClick={() => setMuted(!muted)}
            className={`p-1.5 rounded-lg transition-colors border hover:bg-slate-800 ${muted ? "border-rose-500/40 text-rose-400 bg-rose-950/20" : "border-slate-800 text-teal-400 bg-slate-900"}`}
            title={muted ? "الحالة: صامت" : "الحالة: تفعيل الصوت والمنبهات"}
          >
            {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
          <button 
            type="button"
            onClick={handleTestSound}
            className={`px-2 py-1.5 rounded-lg text-[10px] bg-slate-800 border border-slate-700 font-bold hover:bg-slate-700 transition flex items-center gap-1 ${testSoundActive ? "text-indigo-300 border-indigo-500 bg-indigo-950/20 animate-pulse" : ""}`}
          >
            <Bell className="w-3 h-3 text-indigo-400" />
            فحص الجرس
          </button>
        </div>
      </div>

      {/* Grid containing appointments timeline left & notepad pad right */}
      <div className="p-4 space-y-4">
        
        {/* Realtime Alert Board */}
        {alertLogs.length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 max-h-[100px] overflow-y-auto space-y-1">
            <div className="flex items-center justify-between text-[10px] font-black text-indigo-900 border-b border-indigo-100 pb-1 mb-1">
              <span>🔔 سجل الإشعارات التلقائية الفعالة (اليوم):</span>
              <Sparkles className="w-3.5 h-3.5 shrink-0 animate-spin text-indigo-600" />
            </div>
            {alertLogs.map((log) => (
              <div key={log.id} className="text-[10px] leading-relaxed select-none text-slate-700 flex items-start gap-1">
                <span className="text-slate-400 font-mono">[{log.timestamp.toLocaleTimeString("ar-IQ", { hour: "numeric", minute: "2-digit", second: "2-digit" })}]</span>
                <span className="font-semibold">{log.msg}</span>
              </div>
            ))}
          </div>
        )}

        {/* Timeline Grid */}
        <div className="space-y-4">
          <label className="block text-[11px] font-black text-slate-700 border-b border-dashed border-slate-200 pb-1 flex items-center gap-1">
            <span>📍 مخطط وجدول مواعيد اليوم:</span>
          </label>
          
          {/* 1. Upcoming Appointments List */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[9.5px] text-indigo-700 font-extrabold" dir="rtl">
              <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
              <span>📅 المواعيد القادمة والمستقبلية ({upcomingAppts.length})</span>
            </div>
            {upcomingAppts.length === 0 ? (
              <div className="bg-slate-50/50 p-3 rounded-lg border border-dashed text-center text-[10.5px] text-slate-400 font-medium">
                لا توجد مواعيد نشطة قادمة اليوم.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 max-h-[160px] overflow-y-auto pr-1">
                {upcomingAppts.map((appt) => {
                  const isSelected = selectedApptId === appt.id;
                  const dateFriendly = appt.parsedDate.toLocaleTimeString("ar-IQ", { hour: "numeric", minute: "2-digit" });
                  return (
                    <div 
                      key={appt.id}
                      onClick={() => setSelectedApptId(appt.id)}
                      className={`p-2.5 rounded-lg border transition-all cursor-pointer flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-right ${isSelected ? "bg-indigo-50 border-indigo-400 ring-1 ring-indigo-400" : "bg-white border-slate-200 hover:bg-slate-50/80"}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-md bg-indigo-50 text-indigo-600">
                          <Clock className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span 
                              className="text-xs font-black text-slate-800 hover:text-indigo-700 hover:underline cursor-pointer"
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                // CLINICAL SECURITY & WORKSPACE DIRECTIVE: Safely trigger clinical consultation workspace via double click.
                                const patient = patients.find(p => p.id === appt.patientId);
                                if (patient) setSelectedPatient(patient);
                              }}
                            >{appt.patientName}</span>
                            <span className="text-[9px] text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded font-mono font-extrabold shrink-0">
                              {(() => {
                                const d = appt.parsedDate;
                                return `${d.getMonth() + 1}/${d.getDate()}`;
                              })()}
                            </span>
                            <span className="text-[9.5px] text-slate-500 font-mono">({appt.department})</span>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-slate-500 font-semibold mt-0.5" dir="ltr">
                            <span className="text-indigo-600 tracking-wide font-black">{dateFriendly}</span>
                            <span>•</span>
                            <span className="font-mono">{appt.phone || "بلا هاتف"}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 self-end sm:self-center">
                        {appt.parsedDate.getTime() < currentTimeStamp ? (
                          <span className="bg-amber-100 text-amber-900 text-[8.5px] font-black px-1.5 py-0.5 rounded border border-amber-300">مستمر للمراجعة (مفتوح)</span>
                        ) : (
                          <span className="bg-indigo-100 text-indigo-800 text-[8.5px] font-black px-1.5 py-0.5 rounded border border-indigo-200">بانتظار الفحص</span>
                        )}
                        {localStorage.getItem(`appt_note_${appt.id}`) && (
                          <span className="bg-purple-50 text-purple-700 text-[8.5px] font-black px-1.5 py-0.5 rounded border border-purple-200">📝 ملاحظة مفكرة</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 2. Past & Completed Appointments List (Isolated Archive) */}
          <div className="space-y-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => setShowPastArchive(!showPastArchive)}
              className="w-full flex items-center justify-between text-[11px] font-black text-slate-500 hover:text-slate-800 bg-slate-50/70 hover:bg-slate-50 p-2 rounded-lg border border-slate-200 transition-all text-right cursor-pointer"
            >
              <div className="flex items-center gap-1.5" dir="rtl">
                <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                <span>🗂️ المواعيد الفائتة ({pastAndCompletedAppts.length})</span>
              </div>
              <span className="text-[10px] text-indigo-600 font-extrabold">
                {showPastArchive ? "▲ إخفاء القائمة" : "▼ عرض القائمة"}
              </span>
            </button>

            {showPastArchive && (
              <div className="space-y-2 p-1.5 bg-slate-50/30 rounded-lg border border-slate-150 animate-in fade-in duration-200">
                {pastAndCompletedAppts.length === 0 ? (
                  <div className="bg-slate-50/50 p-3 rounded-lg border border-dashed text-center text-[10.5px] text-slate-400 font-medium">
                    لا توجد مواعيد فائتة مسجلة اليوم.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2 max-h-[160px] overflow-y-auto pr-1">
                    {pastAndCompletedAppts.map((appt) => {
                      const isSelected = selectedApptId === appt.id;
                      const dateFriendly = appt.parsedDate.toLocaleTimeString("ar-IQ", { hour: "numeric", minute: "2-digit" });
                      const isCompleted = appt.status === "COMPLETED";
                      return (
                        <div 
                          key={appt.id}
                          onClick={() => setSelectedApptId(appt.id)}
                          className={`p-2.5 rounded-lg border transition-all cursor-pointer flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-right ${isSelected ? "bg-slate-100 border-slate-400 ring-1 ring-slate-400" : "bg-white border-slate-200 hover:bg-slate-50/80"}`}
                        >
                          <div className="flex items-center gap-2 opacity-80">
                            <div className={`p-1.5 rounded-md ${isCompleted ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                              <Clock className="w-3.5 h-3.5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs font-black text-slate-700">{appt.patientName}</span>
                                <span className="text-[9px] text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded font-mono font-extrabold shrink-0">
                                  {(() => {
                                    const d = appt.parsedDate;
                                    return `${d.getMonth() + 1}/${d.getDate()}`;
                                  })()}
                                </span>
                                <span className="text-[9.5px] text-slate-500 font-mono">({appt.department})</span>
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-slate-500 font-semibold mt-0.5" dir="ltr">
                                <span className={`${isCompleted ? "text-emerald-700" : "text-rose-700"} tracking-wide font-black`}>{dateFriendly}</span>
                                <span>•</span>
                                <span className="font-mono">{appt.phone || "بلا هاتف"}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5 self-end sm:self-center">
                            {isCompleted ? (
                              <span className="bg-emerald-50 text-emerald-700 text-[8.5px] font-black px-1.5 py-0.5 rounded border border-emerald-200">مكتمل</span>
                            ) : (
                              <span className="bg-rose-50 text-rose-700 text-[8.5px] font-black px-1.5 py-0.5 rounded border border-rose-200">فائت (غير مكتمل)</span>
                            )}
                            {localStorage.getItem(`appt_note_${appt.id}`) && (
                              <span className="bg-purple-50 text-purple-700 text-[8.5px] font-black px-1.5 py-0.5 rounded border border-purple-200">📝 ملاحظة مفكرة</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Selected Appointment Notebook Workspace - Area Removed as requested */}
        {selectedApptId && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 animate-in fade-in duration-200 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Edit3 className="w-4 h-4 text-indigo-600" />
                <h4 className="text-[11px] font-black text-slate-800">
                  تفاصيل موعد المريض: {sortedAppts.find(a => a.id === selectedApptId)?.patientName}
                </h4>
              </div>
              <button 
                type="button"
                onClick={() => setSelectedApptId("")}
                className="text-xs text-slate-400 font-bold hover:text-slate-600"
              >
                إغلاق
              </button>
            </div>
            {/* Notes Section Removed */}
          </div>
        )}

      </div>
    </div>
  );
}
